import { EditOutlined } from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Divider,
  Form,
  Input,
  notification,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  setAccessToken,
  setPhoneNumber,
  setVuaId,
} from '../../Services/LocalStorageServices';
import { updatePin } from '../../Services/UserProfileServices';
import { Routes } from '../../Utils/Constants/routes';
import PinInput from '../Common/Components/PinInput';
import './index.css';

enum fromFields {
  firstName = 'firstName',
  lastName = 'lastName',
}

interface IProfileProps {
  updateValues: (type: string, value: any) => void;
  changedValues: {
    firstName: string;
    lastName: string;
  };
  visible: boolean;
}

const Profile: React.FC<IProfileProps> = ({
  updateValues,
  changedValues,
  visible,
}) => {
  const { push } = useHistory();

  const [loading, user] = useSelector((state: any) => [
    state.loader.searchLoader,
    state.user.user,
  ]);
  const [visibleForm, setVisibleForm] = useState(false);
  const [form] = Form.useForm();

  const [updatedPin, setUpdatePin] = useState({
    oldPin: '',
    newPin: '',
    confirmNewPin: '',
  });
  const { t } = useTranslation();

  useEffect(() => {
    if (!visible) {
      setVisibleForm(false);
      setUpdatePin({
        oldPin: '',
        newPin: '',
        confirmNewPin: '',
      });
      form.setFieldsValue({ oldPin: '' });
      form.setFieldsValue({ newPin: '' });
      form.setFieldsValue({ confirmNewPin: '' });
    }
  }, [visible, form]);

  const updateUserInfo = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.id === fromFields.firstName
      ? updateValues(fromFields.firstName, event.target.value)
      : updateValues(fromFields.lastName, event.target.value);
  };

  const checkConfirmPassword = (_: any, value: string) => {
    if (value && value !== updatedPin.newPin && value.length === 6)
      return Promise.reject(new Error(t("PIN doesn't match")));
    return Promise.resolve();
  };

  const checkNewPassword = (_: any, value: string) => {
    if (value && value === updatedPin.oldPin)
      return Promise.reject(
        new Error(t('New PIN should not be same as Old PIN'))
      );
    return Promise.resolve();
  };

  const formSubmit = (values: any) => {
    updatePin(values.oldPin, values.newPin).then(() => {
      form.resetFields();
      setVisibleForm(false);
      setAccessToken('');
      setPhoneNumber('');
      setVuaId('');
      push(Routes.login);
      notification.warning({
        message: t('PIN updated successfully'),
      });
    });
  };

  const onNameChange = (e: any) => {
    e.target.value = e.target.value.replace(/[^A-Za-z]+/g, '');
  };

  return (
    <Skeleton active={true} loading={loading}>
      <Descriptions>
        <Descriptions.Item
          labelStyle={{ color: 'rgba(0,0,0,0.5)' }}
          label='FirstName'
          contentStyle={{ color: 'var(--ant-primary-color)' }}
          span={12}
        >
          <Input
            onInput={onNameChange}
            className='inputStyleColorPadding'
            id='firstName'
            value={changedValues.firstName}
            onChange={updateUserInfo}
            bordered={false}
            prefix={<EditOutlined className='inputIconStyle' />}
          />
        </Descriptions.Item>
        <Descriptions.Item
          labelStyle={{ color: 'rgba(0,0,0,0.5)' }}
          label='LastName'
          span={12}
        >
          <Input
            onInput={onNameChange}
            id='lastName'
            className='inputStyleColorPadding'
            value={changedValues.lastName}
            onChange={updateUserInfo}
            bordered={false}
            prefix={<EditOutlined className='inputIconStyle' />}
          />
        </Descriptions.Item>
        <Descriptions.Item
          labelStyle={{ color: 'rgba(0,0,0,0.5)' }}
          label='Dasboard-aa handle'
          span={12}
          contentStyle={{ fontWeight: 600, color: 'var(--ant-primary-color)' }}
        >
          {user.vuaId}
        </Descriptions.Item>
        <Descriptions.Item
          labelStyle={{ color: 'rgba(0,0,0,0.5)' }}
          label='MobileNumber'
          span={12}
          contentStyle={{ fontWeight: 600, color: 'var(--ant-primary-color)' }}
        >
          {user.mobileNumber}
        </Descriptions.Item>
      </Descriptions>{' '}
      {!visibleForm && (
        <Button
          className='updatePinButton'
          // tslint:disable-next-line: jsx-no-lambda
          onClick={() => setVisibleForm(true)}
          icon={<EditOutlined />}
          key='edit'
          danger={true}
        >
          {t('Update PIN')}
        </Button>
      )}
      {visibleForm && (
        <div className='profileUpdateForm'>
          <Divider orientation='left'>{t('Update PIN')}</Divider>
          <Form
            form={form}
            name='basic'
            onFinish={formSubmit}
            autoComplete='off'
          >
            <Space direction='vertical' className='spaceElement'>
              <PinInput
                label='Old Password'
                nameField='oldPin'
                // tslint:disable-next-line: jsx-no-lambda
                onChange={(pin) =>
                  setUpdatePin((old) => ({ ...old, oldPin: pin }))
                }
                addonBefore={<Typography.Text>Old Password</Typography.Text>}
              />
              <PinInput
                label='New Password'
                nameField='newPin'
                customRule={checkNewPassword}
                // tslint:disable-next-line: jsx-no-lambda
                onChange={(pin) =>
                  setUpdatePin((old) => ({ ...old, newPin: pin }))
                }
                addonBefore={<Typography.Text>New Password</Typography.Text>}
              />
              <PinInput
                label='Confirm New Password'
                nameField='confirmNewPin'
                customRule={checkConfirmPassword}
                // tslint:disable-next-line: jsx-no-lambda
                onChange={(pin) =>
                  setUpdatePin((old) => ({ ...old, confirmNewPin: pin }))
                }
                addonBefore={
                  <Typography.Text>Confirm New Password</Typography.Text>
                }
              />
            </Space>
            <Button
              className='updatePinButton marginTop'
              htmlType='submit'
              icon={<EditOutlined />}
              key='edit'
              danger={true}
            >
              {t('Update PIN')}
            </Button>
          </Form>
        </div>
      )}
    </Skeleton>
  );
};

export default Profile;
